/**
 * Control Portal - Settings Page
 * Platform-wide settings management
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { controlApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  Settings, Shield, Bell, Mail, Globe, Database, 
  RefreshCw, Save, Key, Clock, Loader2, Lock, ShieldCheck, Users, ChevronRight
} from 'lucide-react';

export default function SettingsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    platformName: 'Track0tic',
    supportEmail: 'support@track0tic.com',
    alertEmailEnabled: true,
    alertSmsEnabled: false,
    tempBreachThreshold: 2,
    offlineThresholdMinutes: 30,
    sessionTimeoutMinutes: 60,
    maxLoginAttempts: 5,
    enableAuditLog: true,
    enableWebhooks: true,
    dataRetentionDays: 365,
    telemetryIntervalSeconds: 60,
    lockNonTrack0ticLogins: false,
  });

  useEffect(() => {
    fetchSettings();
     
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await controlApi.getSettings();
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await controlApi.updateSettings(settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Settings className="w-8 h-8 text-blue-500" />
            Platform Settings
          </h1>
          <p className="text-slate-400 mt-1">Configure global platform settings</p>
        </div>
        <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700" disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* General Settings */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-blue-400" />
            General Settings
          </h2>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Platform Name</Label>
              <Input 
                value={settings.platformName}
                onChange={(e) => setSettings({...settings, platformName: e.target.value})}
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-300">Support Email</Label>
              <Input 
                value={settings.supportEmail}
                onChange={(e) => setSettings({...settings, supportEmail: e.target.value})}
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
          </div>
        </div>

        {/* Alert Settings */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Bell className="w-5 h-5 text-amber-400" />
            Alert Configuration
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white">Email Alerts</p>
                <p className="text-slate-500 text-sm">Send alerts via email</p>
              </div>
              <Switch 
                checked={settings.alertEmailEnabled}
                onCheckedChange={(v) => setSettings({...settings, alertEmailEnabled: v})}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white">SMS Alerts</p>
                <p className="text-slate-500 text-sm">Send alerts via SMS</p>
              </div>
              <Switch 
                checked={settings.alertSmsEnabled}
                onCheckedChange={(v) => setSettings({...settings, alertSmsEnabled: v})}
              />
            </div>
            <div>
              <Label className="text-slate-300">Temp Breach Threshold (°C deviation)</Label>
              <Input 
                type="number"
                value={settings.tempBreachThreshold}
                onChange={(e) => setSettings({...settings, tempBreachThreshold: parseInt(e.target.value)})}
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-300">Offline Threshold (minutes)</Label>
              <Input 
                type="number"
                value={settings.offlineThresholdMinutes}
                onChange={(e) => setSettings({...settings, offlineThresholdMinutes: parseInt(e.target.value)})}
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
          </div>
        </div>

        {/* Security Settings */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-green-400" />
            Security Settings
          </h2>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Session Timeout (minutes)</Label>
              <Input 
                type="number"
                value={settings.sessionTimeoutMinutes}
                onChange={(e) => setSettings({...settings, sessionTimeoutMinutes: parseInt(e.target.value)})}
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-300">Max Login Attempts</Label>
              <Input 
                type="number"
                value={settings.maxLoginAttempts}
                onChange={(e) => setSettings({...settings, maxLoginAttempts: parseInt(e.target.value)})}
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white">Audit Logging</p>
                <p className="text-slate-500 text-sm">Log all user actions</p>
              </div>
              <Switch 
                checked={settings.enableAuditLog}
                onCheckedChange={(v) => setSettings({...settings, enableAuditLog: v})}
              />
            </div>

            {/* Platform-wide lockdown */}
            <div className={`flex items-center justify-between rounded-lg border p-3 ${settings.lockNonTrack0ticLogins ? 'border-red-500/40 bg-red-500/5' : 'border-slate-700/50'}`}>
              <div className="flex items-start gap-3">
                <Lock className={`w-5 h-5 mt-0.5 ${settings.lockNonTrack0ticLogins ? 'text-red-400' : 'text-slate-400'}`} />
                <div>
                  <p className="text-white font-medium">Lock All Customer Logins</p>
                  <p className="text-slate-500 text-sm">
                    When ON, only Track0tic admins can sign in. Use during incidents or maintenance.
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.lockNonTrack0ticLogins}
                onCheckedChange={(v) => setSettings({...settings, lockNonTrack0ticLogins: v})}
                data-testid="lock-customer-logins-toggle"
              />
            </div>
          </div>
        </div>

        {/* Integration Settings */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Key className="w-5 h-5 text-purple-400" />
            Integration Settings
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white">Webhooks</p>
                <p className="text-slate-500 text-sm">Enable webhook notifications</p>
              </div>
              <Switch 
                checked={settings.enableWebhooks}
                onCheckedChange={(v) => setSettings({...settings, enableWebhooks: v})}
              />
            </div>
            <div>
              <Label className="text-slate-300">Telemetry Interval (seconds)</Label>
              <Input 
                type="number"
                value={settings.telemetryIntervalSeconds}
                onChange={(e) => setSettings({...settings, telemetryIntervalSeconds: parseInt(e.target.value)})}
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-300">Data Retention (days)</Label>
              <Input 
                type="number"
                value={settings.dataRetentionDays}
                onChange={(e) => setSettings({...settings, dataRetentionDays: parseInt(e.target.value)})}
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Access Management */}
      <div className="mt-6 bg-slate-900 rounded-xl border border-slate-800 p-6">
        <h2 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-blue-400" />
          Access Management
        </h2>
        <p className="text-slate-500 text-sm mb-4">
          Manage who can sign in to Track0tic. Internal users are Track0tic staff; customer users live under each Customer.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => navigate('/control/users')}
            className="text-left bg-slate-800/60 hover:bg-slate-800 border border-slate-700 rounded-lg p-4 transition-colors group"
            data-testid="settings-platform-users-link"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-blue-400" />
              </div>
              <ChevronRight className="w-5 h-5 text-slate-500 group-hover:text-white transition-colors" />
            </div>
            <p className="text-white font-medium">Platform Users</p>
            <p className="text-slate-500 text-sm mt-1">
              Track0tic internal staff — admins, support, engineers
            </p>
          </button>

          <button
            onClick={() => navigate('/control/tenants')}
            className="text-left bg-slate-800/60 hover:bg-slate-800 border border-slate-700 rounded-lg p-4 transition-colors group"
            data-testid="settings-customer-users-link"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-emerald-400" />
              </div>
              <ChevronRight className="w-5 h-5 text-slate-500 group-hover:text-white transition-colors" />
            </div>
            <p className="text-white font-medium">Customer Users</p>
            <p className="text-slate-500 text-sm mt-1">
              Each customer has its own user management — open a customer to manage their users
            </p>
          </button>
        </div>
      </div>
    </div>
  );
}
